import { Link } from "react-router-dom";
import {
  Brain, Users, Cpu, Award, Building2, Globe, ScrollText,
  ArrowRight, FlaskConical, BookOpen, FileText, Lightbulb
} from "lucide-react";
import ExecutiveHero from "@/components/ExecutiveHero";

const RESEARCH_HERO =
  "https://images.unsplash.com/photo-1568667256549-094345857637?auto=format&fit=crop&w=2400&q=80";

const PILLARS = [
  { icon: Brain,         id: "wait",       title: "WAIT Theory",
    desc: "The constitutional doctrine of workforce intelligence — Five-Layer Architecture, Workforce Intelligence Index (WII), and Seven Forces convergence.",
    cta: { label: "Read the Doctrine", href: "/wait-theory" } },
  { icon: Users,         id: "wfi",        title: "Workforce Intelligence Research",
    desc: "Empirical research advancing the measurement, development, and transformation of workforce alignment across institutional layers.",
    cta: { label: "Explore SCFR-Journal", href: "/scfr-journal" } },
  { icon: Cpu,           id: "ai",         title: "Human-AI Workforce Integration",
    desc: "How artificial intelligence integrates with human capability — hybrid intelligence alignment, AI readiness, and institutional adoption.",
    cta: { label: "Read Publications", href: "/scfr-journal" } },
  { icon: ScrollText,    id: "credential", title: "Credential Fatigue Research",
    desc: "Investigations into the structural failure of credential-based workforce assumptions and the case for intelligence-based measurement.",
    cta: { label: "Read SCFR Editorials", href: "/scfr-journal" } },
  { icon: Building2,     id: "org",        title: "Organizational Intelligence",
    desc: "Strategic-to-operational alignment, organizational performance under hybrid intelligence systems, and culture-as-architecture.",
    cta: null },
  { icon: Globe,         id: "inst",       title: "Institutional Intelligence",
    desc: "Governance, doctrine, and the macro-systemic conditions under which workforce intelligence flourishes or fails.",
    cta: null },
];

const WORKING_PAPERS = [
  { title: "Foundations of the Workforce Intelligence Index",   status: "In Development" },
  { title: "Cross-Industry WII Benchmarking Framework",         status: "Planned" },
  { title: "AI Readiness in Regulated Workforces",              status: "Planned" },
  { title: "Credentialing Reform in the Human-AI Era",          status: "Planned" },
];

const AGENDA = [
  "Global Workforce Intelligence Index publication",
  "Multi-sector longitudinal alignment studies",
  "Doctrinal expansion of WAIT Theory (Editions 3.0+)",
  "Public-sector workforce intelligence governance frameworks",
  "Cross-institutional Observatory data partnerships",
];

export default function Research() {
  return (
    <div className="min-h-screen zodiac-page" data-testid="research-page">
      <ExecutiveHero
        eyebrow="Research & Publications"
        title={<>The intellectual capital of the</>}
        accentTitle="Zodiac Institute."
        subtitle="Original doctrine, peer-reviewed research, and institutional working papers advancing the science of workforce intelligence in the Human-AI era."
        image={RESEARCH_HERO}
        breadcrumb={[{ label: "Home", href: "/" }, { label: "Research" }]}
        actions={[
          { label: "Read SCFR-Journal", href: "/scfr-journal", testId: "research-scfr-cta" },
          { label: "WAIT Theory", href: "/wait-theory", variant: "ghost" },
        ]}
      />

      {/* Overview */}
      <section className="bg-white py-20" data-testid="research-overview">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-[#1E3A8A]" /> Research Overview
          </div>
          <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold text-[#0F1E47] leading-tight mb-5">
            ZWII is a <span className="italic text-[#991B1B] font-normal">research institution</span>,
            not only a training institution.
          </h2>
          <p className="text-[#475569] leading-relaxed text-lg">
            The Institute's intellectual program advances workforce intelligence as a measurable
            institutional science. Our scholarship spans the doctrinal, empirical, and applied —
            grounded in WAIT Theory and disseminated through the peer-reviewed Society & Credential
            Fatigue Review.
          </p>
        </div>
      </section>

      {/* Research Pillars */}
      <section className="bg-[#FBFAF7] border-y border-[#E5E7EB] py-20" data-testid="research-pillars">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-[#C8A157]" /> Research Programs
          </div>
          <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold text-[#0F1E47] mb-10 leading-tight">
            Six active research programs.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((p) => (
              <div key={p.id} className="zodiac-hairline-card p-6 flex flex-col">
                <div className="w-11 h-11 rounded-lg bg-[#1E3A8A]/[0.08] flex items-center justify-center mb-4">
                  <p.icon className="w-5 h-5 text-[#1E3A8A]" />
                </div>
                <h3 className="zodiac-heading text-lg font-bold text-[#0F1E47] mb-2">{p.title}</h3>
                <p className="text-sm text-[#6B7280] leading-relaxed flex-1">{p.desc}</p>
                {p.cta && (
                  <Link to={p.cta.href} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#991B1B] hover:underline">
                    {p.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCFR */}
      <section className="bg-white py-20" data-testid="research-scfr">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_300px] gap-10 items-center">
          <div>
            <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
              <span className="w-8 h-px bg-[#991B1B]" /> SCFR Journal
            </div>
            <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold text-[#0F1E47] mb-4 leading-tight">
              Society & Credential Fatigue <span className="italic text-[#991B1B] font-normal">Review.</span>
            </h2>
            <p className="text-[#475569] leading-relaxed mb-6">
              SCFR is the Institute's flagship peer-reviewed journal — convening doctrinal
              scholarship, empirical research, and executive analysis on the structural
              transformation of workforce systems. Edited by the Founder, the journal publishes
              cinematic HTML articles and downloadable PDFs.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/scfr-journal">
                <button className="h-11 px-5 rounded-md bg-[#991B1B] text-white hover:bg-[#7F1D1D] font-semibold inline-flex items-center gap-2">
                  Browse Publications <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/scfr/wait-theory-2-constitutional-authority-edition-2026">
                <button className="h-11 px-5 rounded-md border border-[#0F1E47]/15 text-[#0F1E47] hover:bg-[#FBFAF7] inline-flex items-center gap-2">
                  <BookOpen className="w-4 h-4" /> Featured: WAIT Theory 2.0
                </button>
              </Link>
            </div>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-[#0F1E47] to-[#1E3A8A] text-white p-7 text-center">
            <ScrollText className="w-10 h-10 text-[#C8A157] mx-auto mb-3" />
            <p className="text-[10px] uppercase tracking-widest text-[#C8A157] font-semibold">Editorial Authority</p>
            <p className="zodiac-heading text-xl font-bold mt-2">SCFR</p>
            <p className="text-white/70 text-xs mt-2">Volume 1 · Issue 1 · 2026</p>
          </div>
        </div>
      </section>

      {/* Working Papers */}
      <section className="bg-[#FBFAF7] border-y border-[#E5E7EB] py-20" data-testid="research-working-papers">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-[#0F766E]" /> Working Papers
          </div>
          <h2 className="zodiac-heading text-3xl font-bold text-[#0F1E47] mb-8 leading-tight">
            Forthcoming institutional research.
          </h2>
          <div className="space-y-3">
            {WORKING_PAPERS.map((wp, i) => (
              <div key={i} className="zodiac-hairline-card p-5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <FileText className="w-5 h-5 text-[#0F766E] flex-shrink-0" />
                  <p className="text-[#0F1E47] font-medium">{wp.title}</p>
                </div>
                <span className="text-[11px] uppercase tracking-widest font-semibold text-[#6B7280] bg-[#F4F6FB] px-3 py-1 rounded-full">
                  {wp.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Agenda */}
      <section className="bg-white py-20" data-testid="research-agenda">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-[#1E3A8A]" /> Future Research Agenda
          </div>
          <h2 className="zodiac-heading text-3xl font-bold text-[#0F1E47] mb-8 leading-tight">
            What the Institute is <span className="italic text-[#991B1B] font-normal">building next.</span>
          </h2>
          <div className="space-y-3">
            {AGENDA.map((a, i) => (
              <div key={i} className="flex items-start gap-4 p-4 rounded-lg border border-[#E5E7EB] bg-[#FBFAF7]">
                <Lightbulb className="w-5 h-5 text-[#C8A157] mt-0.5 flex-shrink-0" />
                <p className="text-[#0F1E47]">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-[#0F1E47] text-white py-20 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#C8A157]/12 blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FlaskConical className="w-10 h-10 text-[#C8A157] mx-auto mb-5" />
          <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold mb-4 leading-tight">
            Collaborate with the <span className="italic text-[#C8A157] font-normal">Institute.</span>
          </h2>
          <p className="text-white/70 mb-8 leading-relaxed">
            Academic partnerships, joint research, and co-authored studies — open to qualified
            institutions and researchers.
          </p>
          <Link to="/contact">
            <button className="h-12 px-6 rounded-md bg-[#C8A157] text-[#0F1E47] hover:bg-[#E5C275] font-semibold inline-flex items-center gap-2">
              Initiate Research Collaboration <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
